import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/comics';

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const total = heroSlides.length;

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  return (
    <section className="relative h-[420px] md:h-[600px] w-full overflow-hidden">
      {/* Background Images */}
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>

      {/* Content */}
      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-6 md:px-10 max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            {/* Left Content */}
            <div className="flex-1 max-w-xl">
              {/* Avatars row */}
              <div className="flex items-center gap-2 mb-6">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white/80 overflow-hidden bg-background-300">
                    <img
                      src="https://readdy.ai/api/search-image?query=Cute%20young%20person%20avatar%20portrait%20warm%20smile%20soft%20lighting%20Korean%20style%20minimal%20background&width=80&height=80&seq=hero-avatar-01&orientation=squarish"
                      alt="用户头像"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white/80 overflow-hidden bg-background-300">
                    <img
                      src="https://readdy.ai/api/search-image?query=Friendly%20young%20adult%20portrait%20avatar%20with%20gentle%20smile%20soft%20lighting%20Korean%20style%20minimal%20cream%20background&width=80&height=80&seq=hero-avatar-02&orientation=squarish"
                      alt="用户头像"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white/80 overflow-hidden bg-background-300">
                    <img
                      src="https://readdy.ai/api/search-image?query=Cheerful%20young%20woman%20portrait%20avatar%20with%20bright%20smile%20soft%20lighting%20Korean%20style%20minimal%20background&width=80&height=80&seq=hero-avatar-03&orientation=squarish"
                      alt="用户头像"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <span className="text-white/80 text-xs md:text-sm whitespace-nowrap ml-2">
                  580万+ 漫迷正在使用
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white leading-tight mb-4">
                {heroSlides[current].title}
                <br />
                <span className="font-light text-white/90 text-2xl md:text-4xl lg:text-5xl">
                  {heroSlides[current].subtitle}
                </span>
              </h1>

              {/* Tag + Rating */}
              <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium whitespace-nowrap">
                  {heroSlides[current].tag}
                </span>
                <span className="flex items-center gap-1 text-white/90 text-sm">
                  <i className="ri-star-fill text-accent-500"></i>
                  {heroSlides[current].rating}
                </span>
              </div>

              {/* CTA */}
              <Link
                to={`/comic/${heroSlides[current].comicId}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-foreground-950 rounded-full text-sm font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
              >
                立即阅读
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>

            {/* Right Description */}
            <div className="hidden lg:block max-w-xs">
              <p className="text-white/80 text-sm leading-relaxed">
                {heroSlides[current].description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              i === current ? 'w-6 bg-white' : 'bg-white/40'
            }`}
          />
        ))}
      </div>

      {/* Scroll down hint */}
      <div className="absolute bottom-6 right-6 md:right-10 flex flex-col items-center gap-1">
        <span className="text-white/50 text-xs">向下滚动</span>
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white/50 animate-bounce"></i>
        </div>
      </div>
    </section>
  );
}